/**
 * MFA Challenge - Wave 6 (2026-05-27).
 *
 * For users who have already enrolled in MFA. Login.tsx redirects
 * here (with the partial-auth token in the URL fragment) when the
 * login response says `mfaReason = 'mfa_required'`.
 *
 * Wraps POST /v1/auth/mfa/challenge - accepts either a 6-digit TOTP
 * code or a single-use backup code. Mints a full session JWT on
 * success.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  ShieldCheck,
  Loader2,
  AlertTriangle,
  KeyRound,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Mark } from '@/components/brand/Mark';
import { mfaChallenge, setAuth } from '@/lib/api';

function readPartialAuthFromHash(): string | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash.replace(/^#/, '');
  const params = new URLSearchParams(hash);
  return params.get('t');
}

export function MfaChallengePage(): JSX.Element {
  const navigate = useNavigate();
  const [partialAuth, setPartialAuth] = useState<string | null>(null);
  const [mode, setMode] = useState<'totp' | 'backup'>('totp');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const t = readPartialAuthFromHash();
    if (!t) {
      setError('Missing partial-auth token. Sign in again to retry.');
    }
    setPartialAuth(t);
  }, []);

  async function submit(): Promise<void> {
    if (!partialAuth) return;
    if (mode === 'totp' && code.length !== 6) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    if (mode === 'backup' && code.length < 6) {
      setError('Enter one of your saved backup codes.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const result = await mfaChallenge(partialAuth, mode === 'totp' ? { totpCode: code } : { backupCode: code });
      setAuth(result.session.token, result.user, result.session.expiresAt);
      window.location.hash = '';
      navigate({ to: '/' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'verification failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6 aurora">
      <div className="absolute inset-0 mesh-grid opacity-30 pointer-events-none" />
      <div className="w-full max-w-md relative z-10">
        <div className="flex justify-center mb-8">
          <Mark variant="wordmark" size={44} animated />
        </div>

        <GlassCard className="p-6">
          <CardHeader className="p-0 mb-4 text-center">
            <CardTitle className="flex items-center justify-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" /> Multi-factor verification
            </CardTitle>
            <CardDescription>
              {mode === 'totp'
                ? 'Enter the 6-digit code from your authenticator app to finish signing in.'
                : "Enter one of the backup codes you saved when you set up MFA."}
            </CardDescription>
          </CardHeader>

          {error && (
            <div className="mb-4 glass-lite border border-destructive/40 rounded-md p-3 text-xs text-destructive flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <input
            type="text"
            inputMode={mode === 'totp' ? 'numeric' : 'text'}
            maxLength={mode === 'totp' ? 6 : 16}
            value={code}
            onChange={(e) => {
              const v = mode === 'totp' ? e.target.value.replace(/\D/g, '') : e.target.value;
              setCode(v);
            }}
            placeholder={mode === 'totp' ? '000000' : 'backup-code'}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit();
            }}
            className="glass-lite w-full px-4 py-3 rounded-md text-center text-xl font-mono tracking-widest border border-border/40 focus:outline-none focus:border-primary"
          />

          <Button
            className="w-full mt-4"
            onClick={() => void submit()}
            disabled={loading || !partialAuth || code.length < 6}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
            {loading ? 'Verifying…' : 'Verify & sign in'}
          </Button>

          <button
            type="button"
            className="mt-3 text-[11px] text-primary underline w-full text-center"
            onClick={() => {
              setMode(mode === 'totp' ? 'backup' : 'totp');
              setCode('');
              setError('');
            }}
          >
            {mode === 'totp' ? (
              <>
                <KeyRound className="h-3 w-3 inline mr-1" />
                Use a backup code instead
              </>
            ) : (
              'Back to authenticator code'
            )}
          </button>
        </GlassCard>
      </div>
    </div>
  );
}
